import clsx from 'clsx';
import { type CSSProperties, forwardRef } from 'react';

import { useProgressAriaProps } from './hooks/useProgressAriaProps.js';
import {
  progressCircleBackgroundCSS,
  progressCircleContainerCSS,
  progressCircleCSS,
  progressCircleRootCSS,
  progressCircleSvgCss,
} from './ProgressCircle.sty.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { type AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { type DataTestId } from '../../core/types/data-props.js';
import { type DOMProps } from '../../core/types/dom.js';
import { type MaskingProps } from '../../core/types/masking-props.js';
import { type StylingProps } from '../../core/types/styling-props.js';
import { type WithChildren } from '../../core/types/with-children.js';
import { useContainerColoring } from '../../layouts/container/Container.js';
import { containerColorsCSS } from '../../styles/container.sty.js';
import { textStyle } from '../../styles/textStyle.sty.js';

const COMPONENT_NAME = 'ProgressCircle';

/**
 * Accepted properties for ProgressCircle.
 * @public
 */
export interface ProgressCircleProps
  extends WithChildren,
    DOMProps,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /**
   * The current value.
   * @defaultValue 'indeterminate'
   */
  value?: number | 'indeterminate';
  /**
   * The smallest allowed value.
   * @defaultValue 0
   */
  min?: number;
  /**
   * The maximum allowed value.
   * @defaultValue 100
   */
  max?: number;
  /**
   * Controls the size of the rendered progress.
   * @defaultValue 'large'
   */
  size?: 'small' | 'large';
  /**
   * Controls the color of the progress.
   * @defaultValue 'primary' or a container's color.
   */
  color?: 'neutral' | 'primary' | 'success' | 'warning' | 'critical';
  /**
   * The aria-valuetext attribute defines the human-readable text alternative of aria-valuenow for a range widget.
   * Default the value as percentage will be provided. If the value is not a percentage please provide the aria-valuetext.
   * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-valuetext
   * @defaultValue the value of the progress as percentage.
   */
  'aria-valuetext'?: string;
}

const progressCircleSizes = {
  small: 20,
  large: 40,
};
const progressCircleThickness = 5;

/**
 * The `ProgressCircle` component is used to indicate the progress or completion status of a task or process.
 * @public
 * */
export const ProgressCircle = forwardRef<HTMLDivElement, ProgressCircleProps>(
  (props, forwardedRef) => {
    const {
      value: originalValue,
      min = 0,
      max = 100,
      size = 'large',
      color,
      children,
      id: propId,
      'aria-valuetext': ariaValuetext,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    }: ProgressCircleProps = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    const { value, isIndeterminate, progressAriaProps } = useProgressAriaProps(
      originalValue,
      min,
      max,
      ariaValuetext,
    );

    const containerColoring = useContainerColoring();
    const derivedVariant =
      containerColoring.variant === 'accent' ? 'accent' : 'default';

    // for the color in the ProgressCircle the fallback logic is a bit different - usually
    // the fallback from the container is "neutral" - but in the case of the progress circle the default
    // fallback color is "primary"
    const fallbackColor = containerColoring.default
      ? 'primary'
      : containerColoring.color;
    const derivedColor = color ?? fallbackColor;

    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    const generatedId = useId();
    const id = propId ?? generatedId;

    const circleStyles: CSSProperties = {};

    if (!isIndeterminate && value !== undefined) {
      const circumference =
        2 *
        Math.PI *
        ((progressCircleSizes[size] - progressCircleThickness) / 2);
      circleStyles['strokeDasharray'] = circumference.toFixed(3);
      const dashOffset = Number(
        (((max - value) / max) * circumference).toFixed(3),
      );
      circleStyles['strokeDashoffset'] = `${
        dashOffset >= 0 ? dashOffset : 0
      }px`;
    }

    if (
      !children &&
      !ariaLabelingProps['aria-label'] &&
      !ariaLabelingProps['aria-labelledby']
    ) {
      process.env.NODE_ENV === 'development' &&
        // eslint-disable-next-line no-console
        console.warn(
          'If no label is provided please make sure to provide an aria label on your progressCircle element.',
        );
    }

    return (
      <div
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        ref={forwardedRef}
        className={clsx(
          consumerClassName,
          containerColorsCSS({
            color: derivedColor,
            variant: derivedVariant,
          }),
          progressCircleContainerCSS({
            size,
            color: derivedColor,
            variant: derivedVariant,
          }),
        )}
        style={consumerStyle}
        {...behavioralTrackingProps}
        {...forwardProps}
        data-dt-component={COMPONENT_NAME}
      >
        <span
          id={id}
          className={clsx(progressCircleRootCSS)}
          style={{
            height: `${progressCircleSizes[size]}px`,
            width: `${progressCircleSizes[size]}px`,
          }}
          {...ariaLabelingProps}
          {...progressAriaProps}
        >
          <svg
            className={progressCircleSvgCss}
            viewBox={`0 0 ${progressCircleSizes[size]} ${progressCircleSizes[size]}`}
            width="100%"
            height="100%"
          >
            <circle
              fill="none"
              className={clsx(progressCircleBackgroundCSS)}
              cx={progressCircleSizes[size] / 2}
              cy={progressCircleSizes[size] / 2}
              r={(progressCircleSizes[size] - progressCircleThickness) / 2}
              strokeWidth={progressCircleThickness}
            />
            <circle
              style={circleStyles}
              className={progressCircleCSS({ indeterminate: isIndeterminate })}
              fill="none"
              pathLength={
                isIndeterminate ? 360 : circleStyles['strokeDasharray']
              }
              cx={progressCircleSizes[size] / 2}
              cy={progressCircleSizes[size] / 2}
              r={(progressCircleSizes[size] - progressCircleThickness) / 2}
              strokeWidth={progressCircleThickness}
            />
          </svg>
        </span>
        {children && (
          <label
            className={clsx(
              textStyle({ textStyle: size === 'small' ? 'small' : 'base' }),
            )}
            htmlFor={id}
          >
            {children}
          </label>
        )}
      </div>
    );
  },
);

(
  ProgressCircle as typeof ProgressCircle & { displayName: string }
).displayName = COMPONENT_NAME;
